// Self-attendance window — the ONLY attendance logic computed client-side, and
// only to decide which button to offer. The server re-checks the window on
// POST /sessions/{id}/attendance/self and answers 400 with its own message
// when the click lands outside it; that message is shown verbatim.
import type { AttendanceStatus, SessionStatus } from '@/services/learningService';

export type SelfAttendanceAction = 'confirm' | 'checkin' | null;

/** Check-in opens this many minutes before the session starts. */
export const CHECKIN_LEAD_MINUTES = 15;

const DONE: ReadonlySet<string> = new Set(['attended', 'partially_attended', 'absent', 'excused', 'cancelled']);

/**
 * Which self-service action a participant can take right now:
 *  - 'confirm'  before the session starts (and not yet confirmed/checked in)
 *  - 'checkin'  from 15 minutes before start until the end
 *  - null       otherwise (cancelled session, already recorded, window closed)
 */
export function selfAttendanceAction(
  startsAt: string, endsAt: string, status: SessionStatus | string | undefined, attendance: AttendanceStatus | null | undefined, now: number = Date.now(),
): SelfAttendanceAction {
  if (status === 'cancelled' || status === 'draft') return null;
  if (attendance && DONE.has(attendance)) return null;
  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  if (now > end) return null;
  if (now >= start - CHECKIN_LEAD_MINUTES * 60_000) return 'checkin';
  return attendance === 'confirmed' ? null : 'confirm';
}

/** Whether a session's end falls on the viewer's local "today". */
export function endsToday(endsAt: string, now: number = Date.now()): boolean {
  const e = new Date(endsAt); const n = new Date(now);
  return e.getFullYear() === n.getFullYear() && e.getMonth() === n.getMonth() && e.getDate() === n.getDate();
}
